import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router-dom";
import { useAuth, useInquiryInbox } from "@bw/shared/hooks";
import type { InquiryListParams } from "@bw/shared/api";
import { useSelectedFacility } from "@/app/FacilityContext";
import { getMyFacilityActions } from "@/libs/permissionsApi";

export function InquiryWorkspace() {
  const { user } = useAuth();
  return <InquiryInbox key={user?.uid} />;
}

function InquiryInbox() {
  const { user } = useAuth();
  const { facilityId } = useSelectedFacility();
  const [scope, setScope] = useState<"mine" | "facility">("mine");
  const [status, setStatus] =
    useState<NonNullable<InquiryListParams["lifecycle"]>>("open");
  const permission = useQuery({
    queryKey: ["inquiry-facility-actions", user?.uid, facilityId],
    queryFn: () => getMyFacilityActions(facilityId as string),
    enabled: Boolean(user && facilityId),
    staleTime: 0,
    refetchOnMount: "always",
    retry: false,
  });
  const actions =
    permission.isFetchedAfterMount && !permission.isError
      ? (permission.data?.actions ?? [])
      : [];
  const canUseFacility =
    Boolean(facilityId) &&
    actions.some(
      (action) =>
        action === "facility:inquiry_reply" ||
        action === "facility:inquiry_triage",
    );
  const facilityAccessMessage = !facilityId
    ? "Select a facility to view its shared inbox."
    : permission.isPending || permission.isFetching
      ? "Checking your access to this facility’s shared inbox…"
      : permission.isError
        ? "We couldn’t verify your facility inbox access. Retry the access check."
        : !canUseFacility
          ? "The Facility inbox requires Reply to inquiries or Manage inquiries access. An owner or authorized manager can update your Team roles."
          : null;
  const query = useInquiryInbox(user?.uid, {
    scope,
    lifecycle: status,
    limit: 30,
    ...(scope === "facility"
      ? { facility_id: canUseFacility ? facilityId : null }
      : {}),
  });
  const rows = [
    ...new Map(
      (query.data?.pages.flatMap((page) => page.items) ?? []).map((item) => [
        item.conversation_id,
        item,
      ]),
    ).values(),
  ];
  return (
    <Stack
      spacing={2.5}
      sx={{ p: { xs: 2, md: 3 }, minWidth: 0, maxWidth: 960 }}
    >
      <Stack spacing={0.5}>
        <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
          Inbox
        </Typography>
        <Typography color="text.secondary">
          Reply to player questions and move coaching conversations into
          proposals.
        </Typography>
      </Stack>
      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Stack spacing={2} sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Tabs
            value={scope}
            onChange={(_event, value: "mine" | "facility") => setScope(value)}
            aria-label="Inquiry scope"
          >
            <Tab value="mine" label="Mine" />
            <Tab
              value="facility"
              label="Facility"
              disabled={!canUseFacility}
              aria-describedby={
                !canUseFacility ? "facility-inbox-access-help" : undefined
              }
            />
          </Tabs>
          {facilityAccessMessage ? (
            <Alert
              id="facility-inbox-access-help"
              severity={permission.isError ? "warning" : "info"}
              action={
                permission.isError ? (
                  <Button
                    size="small"
                    onClick={() => void permission.refetch()}
                  >
                    Retry
                  </Button>
                ) : undefined
              }
            >
              {facilityAccessMessage}
            </Alert>
          ) : null}
          <TextField
            select
            label="Status"
            size="small"
            value={status}
            sx={{ width: 180 }}
            onChange={(event) => setStatus(event.target.value as typeof status)}
          >
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
            <MenuItem value="archived">Archived</MenuItem>
            <MenuItem value="all">All history</MenuItem>
          </TextField>
          {scope === "facility" && !canUseFacility ? null : query.isError ? (
            <Alert
              severity="warning"
              action={
                <Button size="small" onClick={() => void query.refetch()}>
                  Retry
                </Button>
              }
            >
              We couldn&apos;t load inquiries. Your access may have changed.
            </Alert>
          ) : query.isPending ? (
            <Typography role="status">Loading inquiries…</Typography>
          ) : (
            <>
              {rows.length ? (
                <List disablePadding>
                  {rows.map((item) => (
                    <ListItemButton
                      key={item.conversation_id}
                      component={RouterLink}
                      to={`/inquiries/${encodeURIComponent(item.conversation_id)}`}
                      divider
                    >
                      <ListItemText
                        primary={item.topic}
                        secondary={`${item.requester_name ?? "Player"} · ${item.facility_name ?? item.target_name ?? "Inquiry"}`}
                        slotProps={{ primary: { sx: { fontWeight: 700 } } }}
                      />
                      {item.unread_count > 0 ? (
                        <Chip
                          size="small"
                          label={`${item.unread_count} unread`}
                        />
                      ) : null}
                    </ListItemButton>
                  ))}
                </List>
              ) : !query.hasNextPage ? (
                <Stack
                  sx={{ alignItems: "center", py: 5, textAlign: "center" }}
                  spacing={1}
                >
                  <ForumOutlinedIcon color="disabled" aria-hidden="true" />
                  <Typography sx={{ fontWeight: 700 }}>
                    No {status === "all" ? "" : `${status} `}inquiries
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    New player questions addressed or assigned to you will
                    appear here.
                  </Typography>
                </Stack>
              ) : null}
              {query.hasNextPage ? (
                <Button
                  size="small"
                  disabled={query.isFetchingNextPage}
                  onClick={() => void query.fetchNextPage()}
                  sx={{ alignSelf: "flex-start" }}
                >
                  Load more
                </Button>
              ) : null}
            </>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
